import React, { useEffect, useMemo, useState } from 'react';
import { Coins, History } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import api from '../../lib/api';
import SubpageHeader from './SubpageHeader';

type PointAccount = {
  id: number;
  balance_points: number;
  lifetime_earned_points: number;
  lifetime_redeemed_points: number;
  lifetime_expired_points: number;
};

type PointTransaction = {
  id: number;
  store_name: string | null;
  transaction_type: 'earn' | 'redeem' | 'expire' | 'refund' | 'adjust' | string;
  points_delta: number;
  balance_after: number;
  amount_basis_usd: number | null;
  expires_at: string | null;
  note: string | null;
  created_at: string;
};

type PointsResponse = {
  visible?: boolean;
  total_balance_points: number;
  total_lifetime_earned_points: number;
  total_lifetime_redeemed_points: number;
  total_lifetime_expired_points: number;
  accounts: PointAccount[];
  transactions: PointTransaction[];
};

const money = (value?: number | null): string => {
  if (value == null) return '-';
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const transactionLabel = (type: PointTransaction['transaction_type']): string => {
  switch (type) {
    case 'earn':
      return 'Earned';
    case 'redeem':
      return 'Used';
    case 'expire':
      return 'Expired';
    case 'refund':
      return 'Refunded';
    case 'adjust':
      return 'Adjusted';
    default:
      return type || 'Updated';
  }
};

const transactionTone = (type: PointTransaction['transaction_type']): string => {
  if (type === 'earn') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (type === 'redeem') return 'border-cyan-200 bg-cyan-50 text-cyan-700';
  if (type === 'expire' || type === 'refund') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const PointsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PointsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadPoints = async () => {
      try {
        const res = await api.get<PointsResponse>('/api/customer-app/points');
        if (active) setData(res.data);
      } catch (error) {
        if (active) toast.error('Failed to load points');
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadPoints();

    return () => {
      active = false;
    };
  }, []);

  const transactions = useMemo(() => data?.transactions ?? [], [data]);

  if (loading) {
    return (
      <div className="space-y-4">
        <SubpageHeader title="Points" onBack={() => navigate('/reserve/mypage')} />
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          Loading points...
        </div>
      </div>
    );
  }

  if (!data?.visible) {
    return (
      <div className="space-y-4">
        <SubpageHeader title="Points" onBack={() => navigate('/reserve/mypage')} />
        <div className="rounded-[30px] border border-slate-200 bg-white px-5 py-10 text-center shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
          <div className="text-base font-semibold text-slate-900">Points are not available</div>
          <div className="mt-2 text-sm text-slate-500">
            This company does not show point information in the customer app.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SubpageHeader title="Points" onBack={() => navigate('/reserve/mypage')} />

      <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div className="bg-[linear-gradient(135deg,#0f172a,#0876D8)] px-5 py-5 text-white">
          <div className="flex items-start gap-3">
            <div className="rounded-[20px] bg-white/15 p-3">
              <Coins className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/65">
                Available Points
              </div>
              <div className="mt-1 text-[34px] font-semibold leading-none tracking-[-0.03em]">
                {(data?.total_balance_points ?? 0).toLocaleString()} pt
              </div>
              <div className="mt-2 text-sm text-white/72">
                Points can be used at checkout when available.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 px-5 py-4">
          <div className="rounded-[22px] bg-slate-50 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Earned
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {(data?.total_lifetime_earned_points ?? 0).toLocaleString()} pt
            </div>
          </div>
          <div className="rounded-[22px] bg-slate-50 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Used
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {(data?.total_lifetime_redeemed_points ?? 0).toLocaleString()} pt
            </div>
          </div>
          <div className="rounded-[22px] bg-slate-50 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Expired
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {(data?.total_lifetime_expired_points ?? 0).toLocaleString()} pt
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
          <History className="h-4 w-4" />
          History
        </div>

        {transactions.length === 0 ? (
          <div className="rounded-[30px] border border-slate-200 bg-white px-5 py-10 text-center shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <div className="text-base font-semibold text-slate-900">No point history yet</div>
            <div className="mt-2 text-sm text-slate-500">
              Earned and used points will appear here.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((item) => (
              <div
                key={item.id}
                className="rounded-[26px] border border-slate-200 bg-white px-4 py-4 shadow-[0_14px_34px_rgba(15,23,42,0.07)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${transactionTone(item.transaction_type)}`}>
                        {transactionLabel(item.transaction_type)}
                      </span>
                      <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                    </div>
                    <div className="mt-2 text-sm font-medium text-slate-900">
                      {item.store_name ?? 'Store'}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Basis {money(item.amount_basis_usd)}
                      {item.expires_at ? ` · Expires ${formatDate(item.expires_at)}` : ''}
                    </div>
                    {item.note ? <div className="mt-1 text-xs text-slate-400">{item.note}</div> : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`text-lg font-semibold ${item.points_delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {item.points_delta >= 0 ? '+' : ''}
                      {item.points_delta.toLocaleString()} pt
                    </div>
                    <div className="text-xs text-slate-400">
                      Balance {item.balance_after.toLocaleString()} pt
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PointsPage;
